import React, { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import {Plus, Edit2, Trash2, Copy, CheckCircle} from 'lucide-react'

interface Coupon {
  _id: string
  code: string
  type: "percentage" | "fixed" | "freeShipping"
  discountValue: number
  minPurchase: number
  applicableProducts?: string[]
  applicableStores?: string[]
  validFrom: string
  validUntil: string
  status: "active" | "inactive" | "expired"
  usageLimit: number
  usedCount: number
  targetAudience: "all" | "newMember" | "vip" | "birthday"
  createdBy?: string
  createdAt: string
  updatedAt: string
}

const typeLabels: Record<string, string> = {
  percentage: "百分比折扣",
  fixed: "固定金額",
  freeShipping: "免運費"
}

const audienceLabels: Record<string, string> = {
  all: "所有會員",
  newMember: "新會員",
  vip: "VIP 會員",
  birthday: "生日會員"
}

const CouponManager: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null)
  const [copiedCode, setCopiedCode] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    code: "",
    type: "percentage" as Coupon["type"],
    discountValue: 0,
    minPurchase: 0,
    validFrom: "",
    validUntil: "",
    status: "active" as Coupon["status"],
    usageLimit: 0,
    targetAudience: "all" as Coupon["targetAudience"]
  })

  useEffect(() => {
    fetchCoupons()
  }, [])

  const fetchCoupons = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.coupons.list({
        sort: { createdAt: -1 }
      })
      setCoupons(list)
    } catch (error) {
      console.error("Failed to fetch coupons:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const now = new Date().toISOString()
      if (editingCoupon) {
        await lumi.entities.coupons.update(editingCoupon._id, {
          ...formData,
          updatedAt: now
        })
      } else {
        await lumi.entities.coupons.create({
          ...formData,
          applicableProducts: [],
          applicableStores: [],
          usedCount: 0,
          createdBy: "admin",
          createdAt: now,
          updatedAt: now
        })
      }
      await fetchCoupons()
      resetForm()
    } catch (error) {
      console.error("Failed to save coupon:", error)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm("確定要刪除此優惠券嗎？")) return
    try {
      await lumi.entities.coupons.delete(id)
      await fetchCoupons()
    } catch (error) {
      console.error("Failed to delete coupon:", error)
    }
  }

  const handleEdit = (coupon: Coupon) => {
    setEditingCoupon(coupon)
    setFormData({
      code: coupon.code,
      type: coupon.type,
      discountValue: coupon.discountValue,
      minPurchase: coupon.minPurchase,
      validFrom: coupon.validFrom.split("T")[0],
      validUntil: coupon.validUntil.split("T")[0],
      status: coupon.status,
      usageLimit: coupon.usageLimit,
      targetAudience: coupon.targetAudience
    })
    setShowForm(true)
  }

  const resetForm = () => {
    setShowForm(false)
    setEditingCoupon(null)
    setFormData({
      code: "",
      type: "percentage",
      discountValue: 0,
      minPurchase: 0,
      validFrom: "",
      validUntil: "",
      status: "active",
      usageLimit: 0,
      targetAudience: "all"
    })
  }

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code)
    setCopiedCode(code)
    setTimeout(() => setCopiedCode(null), 2000)
  }

  const getStatusColor = (coupon: Coupon) => {
    if (coupon.status === "inactive") return "bg-gray-600"
    if (new Date(coupon.validUntil) < new Date()) return "bg-red-600"
    if (coupon.usageLimit > 0 && (coupon.usedCount || 0) >= coupon.usageLimit) return "bg-orange-600"
    return "bg-green-600"
  }

  const getStatusText = (coupon: Coupon) => {
    if (coupon.status === "inactive") return "停用"
    if (new Date(coupon.validUntil) < new Date()) return "已過期"
    if (coupon.usageLimit > 0 && (coupon.usedCount || 0) >= coupon.usageLimit) return "已用完"
    return "啟用中"
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">載入中...</div>
      </div>
    )
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-light tracking-wider mb-2">優惠券管理</h2>
          <p className="text-gray-400">建立與管理行銷優惠券</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-6 py-3 bg-orange-600 text-white hover:bg-orange-700 transition-colors"
        >
          <Plus size={20} />
          新增優惠券
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-950 border border-gray-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-800">
              <h3 className="text-xl font-light tracking-wider">
                {editingCoupon ? "編輯優惠券" : "新增優惠券"}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">優惠券代碼</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 bg-black border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600 uppercase"
                    placeholder="WELCOME2025"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">類型</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as Coupon["type"] })}
                    className="w-full px-4 py-2 bg-black border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                  >
                    <option value="percentage">百分比折扣</option>
                    <option value="fixed">固定金額</option>
                    <option value="freeShipping">免運費</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    折扣值 {formData.type === "percentage" ? "(%)" : formData.type === "fixed" ? "(元)" : ""}
                  </label>
                  <input
                    type="number"
                    value={formData.discountValue}
                    onChange={(e) => setFormData({ ...formData, discountValue: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 bg-black border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                    min="0"
                    step={formData.type === "percentage" ? "1" : "10"}
                    disabled={formData.type === "freeShipping"}
                    required={formData.type !== "freeShipping"}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">最低消費門檻 (元)</label>
                  <input
                    type="number"
                    value={formData.minPurchase}
                    onChange={(e) => setFormData({ ...formData, minPurchase: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 bg-black border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                    min="0"
                    step="100"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">有效起始日期</label>
                  <input
                    type="date"
                    value={formData.validFrom}
                    onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                    className="w-full px-4 py-2 bg-black border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">有效結束日期</label>
                  <input
                    type="date"
                    value={formData.validUntil}
                    onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                    className="w-full px-4 py-2 bg-black border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">使用次數限制 (0=無限制)</label>
                  <input
                    type="number"
                    value={formData.usageLimit}
                    onChange={(e) => setFormData({ ...formData, usageLimit: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 bg-black border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                    min="0"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">目標受眾</label>
                  <select
                    value={formData.targetAudience}
                    onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value as Coupon["targetAudience"] })}
                    className="w-full px-4 py-2 bg-black border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                  >
                    <option value="all">所有會員</option>
                    <option value="newMember">新會員</option>
                    <option value="vip">VIP 會員</option>
                    <option value="birthday">生日會員</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">狀態</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as Coupon["status"] })}
                  className="w-full px-4 py-2 bg-black border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                >
                  <option value="active">啟用</option>
                  <option value="inactive">停用</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-800">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                >
                  {editingCoupon ? "更新" : "建立"}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-6 py-3 bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  取消
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-gray-950 border border-gray-800 p-6">
          <div className="text-3xl font-light text-gray-100 mb-2">{coupons.length}</div>
          <div className="text-sm text-gray-400">總優惠券數</div>
        </div>
        <div className="bg-gray-950 border border-gray-800 p-6">
          <div className="text-3xl font-light text-gray-100 mb-2">
            {coupons.filter((c) => c.status === "active" && new Date(c.validUntil) > new Date()).length}
          </div>
          <div className="text-sm text-gray-400">啟用中</div>
        </div>
        <div className="bg-gray-950 border border-gray-800 p-6">
          <div className="text-3xl font-light text-gray-100 mb-2">
            {coupons.reduce((sum, c) => sum + (c.usedCount || 0), 0)}
          </div>
          <div className="text-sm text-gray-400">總使用次數</div>
        </div>
        <div className="bg-gray-950 border border-gray-800 p-6">
          <div className="text-3xl font-light text-gray-100 mb-2">
            {coupons.filter((c) => new Date(c.validUntil) < new Date()).length}
          </div>
          <div className="text-sm text-gray-400">已過期</div>
        </div>
      </div>

      <div className="bg-gray-950 border border-gray-800">
        <table className="w-full">
          <thead className="border-b border-gray-800">
            <tr className="text-left">
              <th className="px-6 py-4 text-sm font-light text-gray-400">優惠券代碼</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">類型</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">折扣</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">使用狀況</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">有效期限</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">狀態</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">操作</th>
            </tr>
          </thead>
          <tbody>
            {coupons.map((coupon) => (
              <tr key={coupon._id} className="border-b border-gray-800 hover:bg-gray-900 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <code className="px-3 py-1 bg-gray-800 text-orange-400 font-mono text-sm">
                      {coupon.code}
                    </code>
                    <button
                      onClick={() => copyCode(coupon.code)}
                      className="p-1 hover:bg-gray-800 transition-colors"
                      title="複製代碼"
                    >
                      {copiedCode === coupon.code ? (
                        <CheckCircle size={14} className="text-green-500" />
                      ) : (
                        <Copy size={14} className="text-gray-500" />
                      )}
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-300 text-sm">{typeLabels[coupon.type]}</td>
                <td className="px-6 py-4 text-gray-300">
                  {coupon.type === "percentage"
                    ? `${coupon.discountValue}%`
                    : coupon.type === "fixed"
                    ? `$${coupon.discountValue}`
                    : "免運"}
                </td>
                <td className="px-6 py-4 text-gray-300 text-sm">
                  {coupon.usedCount || 0} / {coupon.usageLimit === 0 ? "∞" : coupon.usageLimit}
                </td>
                <td className="px-6 py-4 text-gray-400 text-sm">
                  {new Date(coupon.validFrom).toLocaleDateString("zh-TW")} ~{" "}
                  {new Date(coupon.validUntil).toLocaleDateString("zh-TW")}
                </td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 ${getStatusColor(coupon)} text-white text-xs`}>
                    {getStatusText(coupon)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(coupon)}
                      className="p-2 hover:bg-gray-800 transition-colors"
                      title="編輯"
                    >
                      <Edit2 size={16} className="text-gray-400" />
                    </button>
                    <button
                      onClick={() => handleDelete(coupon._id)}
                      className="p-2 hover:bg-gray-800 transition-colors"
                      title="刪除"
                    >
                      <Trash2 size={16} className="text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {coupons.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            尚無優惠券資料
          </div>
        )}
      </div>
    </div>
  )
}

export default CouponManager
